import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Package, MapPin, Phone, User, Clock, ChevronDown, ChevronUp, Mail } from 'lucide-react';
import { API_URL, ADMIN_TOKEN_KEY, adminFetch, statusColors, statusOptions } from './shared';

export const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // all | today | 7d | 30d
  const [paymentFilter, setPaymentFilter] = useState('all'); // all | paypal | mercadopago | transfer

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 10000);
      const res = await adminFetch(`/api/orders`, { cache: 'no-store', signal: ctrl.signal });
      clearTimeout(t);
      if (res.ok) setOrders(await res.json());
    } catch (err) {
      console.error('fetchOrders error:', err);
    } finally { setLoading(false); }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      const res = await adminFetch(`/api/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(prev => prev.map(o => o.id === orderId ? { ...o, order_status: newStatus } : o));
        if (data.email_sent) {
          // Visual feedback for email sent (optional)
        }
      }
    } catch (err) { console.error('Error:', err); }
  };

  const traditionLabels = { ashkenazi: 'Ashkenazi', sefaradi: 'Sefaradí', no_amarrados: 'No Amarrados' };
  const hilosLabels = { avodat_yad_grueso: 'A Mano Gruesos', avodat_yad_delgado: 'A Mano Delgados', mechona_delgado: 'A Máquina', sin_hilos: 'Sin Hilos' };
  const cottonLabels = { cotton_smooth: 'Liso', cotton_edge: 'Acanalado' };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  const paymentStatusLabel = (status) => {
    if (status === 'PENDING') return 'Pago Mercado Pago pendiente';
    if (status === 'PENDING_TRANSFER') return 'Transferencia pendiente';
    return status;
  };

  // Apply filters: status + free-text search (name, email, phone, order ID) + date range + payment method
  const q = searchQuery.trim().toLowerCase();
  const nowMs = Date.now();
  const getDateThreshold = () => {
    if (dateFilter === 'today') return new Date(new Date().setHours(0, 0, 0, 0)).getTime();
    if (dateFilter === '7d') return nowMs - 7 * 24 * 60 * 60 * 1000;
    if (dateFilter === '30d') return nowMs - 30 * 24 * 60 * 60 * 1000;
    return null;
  };
  const dateThreshold = getDateThreshold();

  const paymentOf = (o) => {
    // Order created via /api/paypal has "paypal_order_id"; MP has "preference_id" or status PENDING; transfer has status PENDING_TRANSFER
    if (o.paypal_order_id || o.payment_method === 'paypal') return 'paypal';
    if (o.status === 'PENDING_TRANSFER' || o.payment_method === 'transfer') return 'transfer';
    if (o.preference_id || o.payment_method === 'mercadopago' || o.status === 'PENDING') return 'mercadopago';
    return 'other';
  };

  const visibleOrders = orders.filter(o => {
    const status = o.order_status || 'Nuevo';
    if (statusFilter !== 'all' && status !== statusFilter) return false;
    if (dateThreshold !== null) {
      const ts = o.created_at ? new Date(o.created_at).getTime() : 0;
      if (ts < dateThreshold) return false;
    }
    if (paymentFilter !== 'all' && paymentOf(o) !== paymentFilter) return false;
    if (q) {
      const hay = [
        o.customer?.name, o.customer?.email, o.customer?.phone,
        o.id, o.order_status, o.status
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Status counts for filter badges (computed against all orders, not filtered)
  const statusCounts = orders.reduce((acc, o) => {
    const s = o.order_status || 'Nuevo';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      {/* Filters toolbar */}
      <div className="mb-4 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-sm text-slate-500" data-testid="orders-count">
            <strong>{visibleOrders.length}</strong> de {orders.length} pedidos
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Buscar por nombre, email, teléfono o ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-72 max-w-full px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
              data-testid="orders-search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="text-xs text-slate-500 hover:text-slate-700" data-testid="orders-search-clear">
                Limpiar
              </button>
            )}
            <Button variant="outline" size="sm" onClick={fetchOrders} data-testid="refresh-orders">Actualizar</Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const token = sessionStorage.getItem(ADMIN_TOKEN_KEY) || '';
                const params = new URLSearchParams();
                if (statusFilter !== 'all') params.set('status', statusFilter);
                const url = `${API_URL}/api/orders/export.csv${params.toString() ? `?${params}` : ''}`;
                try {
                  const res = await fetch(url, { headers: { 'X-Admin-Token': token } });
                  if (!res.ok) throw new Error(`HTTP ${res.status}`);
                  const blob = await res.blob();
                  const link = document.createElement('a');
                  link.href = URL.createObjectURL(blob);
                  link.download = res.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] || `pedidos-${new Date().toISOString().slice(0,10)}.csv`;
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
                } catch (e) {
                  alert(`Error al exportar: ${e.message}`);
                }
              }}
              data-testid="export-orders-csv"
              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
            >
              ⬇ Exportar CSV
            </Button>
          </div>
        </div>
        {/* Status filter pills */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-colors ${statusFilter === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid="status-filter-all"
          >
            Todos ({orders.length})
          </button>
          {statusOptions.map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-colors ${statusFilter === s ? `${statusColors[s]} text-white` : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`status-filter-${s.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {s} ({statusCounts[s] || 0})
            </button>
          ))}
        </div>
        {/* Date range + Payment method pills */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-1">Fecha:</span>
          {[
            { id: 'all', label: 'Todas' },
            { id: 'today', label: 'Hoy' },
            { id: '7d', label: 'Últimos 7 días' },
            { id: '30d', label: 'Últimos 30 días' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setDateFilter(f.id)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-colors ${dateFilter === f.id ? 'bg-cyan-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`date-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider ml-3 mr-1">Pago:</span>
          {[
            { id: 'all', label: 'Todos' },
            { id: 'paypal', label: 'PayPal' },
            { id: 'mercadopago', label: 'Mercado Pago' },
            { id: 'transfer', label: 'Transferencia' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setPaymentFilter(f.id)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-colors ${paymentFilter === f.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`payment-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Cargando pedidos...</div>
      ) : visibleOrders.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No hay pedidos todavía</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleOrders.map((order) => (
            <Card key={order.id} className="bg-white overflow-hidden" data-testid={`order-${order.id}`}>
              <div className="p-4 cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={`${statusColors[order.order_status || 'Nuevo']} text-white text-xs`}>
                      {order.order_status || 'Nuevo'}
                    </Badge>
                    {order.status && order.status !== 'COMPLETED' && (
                      <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 bg-amber-50" data-testid={`payment-status-${order.id}`}>
                        {paymentStatusLabel(order.status)}
                      </Badge>
                    )}
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{order.shipping_address?.fullName || 'Sin nombre'}</p>
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Clock className="w-3 h-3" />{formatDate(order.created_at)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-cyan-600">${order.total?.toFixed(2)} {order.currency}</span>
                    {expandedOrder === order.id ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </div>
                </div>
              </div>

              {expandedOrder === order.id && (
                <div className="border-t border-slate-100 p-4 space-y-4">
                  {/* Status Update */}
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Cambiar estado:</label>
                    <div className="flex flex-wrap gap-2">
                      {statusOptions.map(s => (
                        <button key={s} onClick={() => updateStatus(order.id, s)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            (order.order_status || 'Nuevo') === s ? `${statusColors[s]} text-white` : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`} data-testid={`status-${s}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Customer Info */}
                  {order.shipping_address && (
                    <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
                      <div className="flex items-center gap-2 text-sm"><User className="w-4 h-4 text-slate-400" /><span className="font-medium">{order.shipping_address.fullName}</span></div>
                      {order.shipping_address.email && <div className="flex items-center gap-2 text-sm text-slate-600"><Mail className="w-4 h-4 text-slate-400" />{order.shipping_address.email}</div>}
                      <div className="flex items-center gap-2 text-sm text-slate-600"><Phone className="w-4 h-4 text-slate-400" /><a href={`tel:${order.shipping_address.phone}`} className="text-cyan-600 hover:underline">{order.shipping_address.phone}</a></div>
                      <div className="flex items-start gap-2 text-sm text-slate-600"><MapPin className="w-4 h-4 text-slate-400 mt-0.5" /><span>{order.shipping_address.street}, {order.shipping_address.zona && `${order.shipping_address.zona}, `}{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}</span></div>
                    </div>
                  )}

                  {/* Products */}
                  <div>
                    <h4 className="text-xs font-medium text-slate-600 mb-2">Productos:</h4>
                    <div className="space-y-2">
                      {order.items?.map((item, idx) => (
                        <div key={`${order.id}-item-${item.name}-${item.size}-${idx}`} className="bg-slate-50 rounded-lg p-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-sm text-slate-800">{item.name}</p>
                              <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                                <p>Talla: {item.size}</p>
                                {item.color && <p>Color: {item.color}</p>}
                                {item.tradition && <p>Tradición: {traditionLabels[item.tradition] || item.tradition}</p>}
                                {item.threads && <p>Hilos: {hilosLabels[item.threads] || item.threads}</p>}
                                {item.cottonType && <p>Algodón: {cottonLabels[item.cottonType] || item.cottonType}</p>}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-cyan-600 text-sm">${(item.price * item.quantity).toFixed(2)}</p>
                              <p className="text-xs text-slate-500">x{item.quantity}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {order.payer_email && <p className="text-xs text-slate-500">PayPal: {order.payer_email}</p>}
                  {order.payment_method && <p className="text-xs text-slate-500">Método: {order.payment_method}</p>}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== CONTACTS TAB ====================
